"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getProductSchema = exports.getProductListSchema = void 0;
const url_generators_1 = require("../url-generators");
const mosaic_1 = require("../utils/mosaic");
const merchant_return_policy_schema_1 = require("./merchant-return-policy-schema");
const offer_schema_1 = require("./offer-schema");
const rating_schema_1 = require("./rating-schema");
const reviews_schema_1 = require("./reviews-schema");
const shipping_details_schema_1 = require("./shipping-details-schema");
/**
 * Converts a product list object into a ProductList schema. If the input is a single product,
 * please use getProductSchema instead, or undefined will be returned.
 *
 * @param inputObject the product list object to convert
 * @returns the ProductList schema JSON string or undefined if the input object is invalid
 */
function getProductListSchema(inputObject) {
    if (isValidProductList(inputObject) === false) {
        return undefined;
    }
    const convertedProductList = inputObject;
    return JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        itemListElement: convertedProductList
            .filter((product) => product?.name &&
            product?.href &&
            product?.price &&
            (product?.image || product?.images) &&
            product?.inStock !== undefined)
            .map((product, idx) => {
            return {
                '@type': 'ListItem',
                position: idx + 1,
                item: {
                    '@type': 'Product',
                    name: product.name,
                    image: getImageFromProduct(product),
                    offers: (0, offer_schema_1.getOffersSchemaForProductList)(product.inStock, product.price),
                    ...(0, rating_schema_1.getAggregateRatingSchema)(product.rating || 0, product.ratingCount || 0),
                    url: product.href,
                    ...(0, merchant_return_policy_schema_1.getMerchantReturnPolicySchema)(),
                    ...(0, shipping_details_schema_1.getShippingDetailsSchema)(),
                },
            };
        }),
    });
}
exports.getProductListSchema = getProductListSchema;
/**
 * The function will pick required fields from inputObject, and function will gracefully handle if any of them do not exist.
 *
 * The inputObject could also specify productUrl, metaDescription, pageCatalogEntryId and reviews fields for more accurate schema generation.
 * These fields are not mandatory as the function has fallbacks for them.
 *
 * To append these fields in the inputObject, please use the following format:
 *
 * getProductSchema({...product, productUrl: 'product chewy.com url', metaDescription: 'description', pageCatalogEntryId: '28801', reviews: reviewNode[]})
 *
 * - productUrl: the URL of the product page that PDP team is currently responsible for, fallback might not keep updated with the latest URL logic.
 * - metaDescription: the meta description of the product which has RX related logic in PDP, fallback is the product description.
 * - pageCatalogEntryId: the catalog entry ID of the product fetched from the URL, fallback is the product entry ID.
 * - reviews: the reviews of the product which are fetched in PDP, has to input from PDP as reviewNode[], fallback is empty array meaning no reviews will be included in the schema
 *
 * @param inputObect
 * @returns
 */
function getProductSchema(inputObect) {
    if (isValidProduct(inputObect) === false) {
        return undefined;
    }
    const convertedProduct = inputObect;
    // PDP URL is current responsible by the PDP team
    // however, we also provide a backup URL in case the PDP URL is passed in
    const productUrl = convertedProduct?.productUrl ||
        (0, url_generators_1.generateProductUrl)(convertedProduct?.slug, convertedProduct?.entryID);
    const pageCatalogEntryId = convertedProduct?.pageCatalogEntryId || convertedProduct?.entryID;
    const productVariants = generateVariants(convertedProduct, convertedProduct?.metaDescription);
    const productTypeDetails = convertedProduct.product?.items?.length === 1 || productVariants.length === 0
        ? {
            '@type': 'Product',
            sku: convertedProduct?.partNumber,
            gtin12: convertedProduct?.gtin ?? '',
        }
        : {
            '@type': 'ProductGroup',
            productGroupID: convertedProduct.product?.partNumber,
            hasVariant: productVariants,
        };
    const offersSchema = (0, offer_schema_1.getOffersSchemaForProduct)(convertedProduct, pageCatalogEntryId, productUrl);
    const productSchema = {
        '@context': 'https://schema.org/',
        ...productTypeDetails,
        name: convertedProduct.product?.name,
        image: convertedProduct.images?.[0]?.metaLarge ??
            'https://www.chewy.com/assets/img/logo/chewy-opengraph.20170505.png',
        brand: {
            '@type': 'Brand',
            name: convertedProduct.product?.manufacturerName,
        },
        category: getCategorySchema(convertedProduct),
        ...(0, rating_schema_1.getAggregateRatingSchema)(convertedProduct.product?.rating || 0, convertedProduct.product?.ratingCount || 0),
        description: convertedProduct.product?.description || convertedProduct?.metaDescription,
        ...(offersSchema && { offers: offersSchema }),
        ...(convertedProduct?.reviews && (0, reviews_schema_1.getReviewSchema)(convertedProduct?.reviews)),
    };
    return JSON.stringify(productSchema);
}
exports.getProductSchema = getProductSchema;
const generateVariants = (product, metaDescription) => {
    const parentProduct = product?.product;
    if (!parentProduct) {
        return [];
    }
    const mda = parentProduct?.mda ?? [];
    const filteredMda = parentProduct.mda
        ?.filter((it) => !it?.isEnsemble)
        .map((it) => it?.name?.toLowerCase())
        .filter((it) => !!it) ?? [];
    const childItems = parentProduct?.items ?? [];
    const itemDefiningAttributes = product.definingAttributeValues ?? [];
    const ensembles = new Set(mda
        .filter((it) => it?.isEnsemble)
        .map((it) => it?.name?.toLowerCase())
        .filter((it) => !!it));
    const itemEnsembles = itemDefiningAttributes.filter((att) => !!att?.attribute?.name && ensembles.has(att?.attribute?.name?.toLowerCase()));
    return childItems
        .filter((it) => !!it && isNotEnsembleProduct(itemEnsembles, it.definingAttributeValues))
        .map((it) => {
        const descriptionData = it?.description || metaDescription;
        return {
            '@context': 'https://schema.org',
            '@type': 'Product',
            name: it?.name,
            ...(descriptionData && { description: descriptionData }),
            image: it?.metaImageLarge?.url ??
                'https://www.chewy.com/assets/img/logo/chewy-opengraph.20170505.png',
            sku: it?.partNumber,
            gtin13: it?.gtin ?? '',
            offers: [(0, offer_schema_1.generateOfferSchema)(it?.advertisedPrice || '0', '', it?.inStock, true, true)],
            ...getChildVariesByProductData(filteredMda, it?.definingAttributeValues),
        };
    });
};
const getChildVariesByProductData = (mdaSeoData, definingAttributeValues) => {
    const variesByProductData = {};
    mdaSeoData.forEach((mda) => {
        const value = definingAttributeValues?.find((att) => att?.attribute?.name?.toLowerCase() === mda)?.value;
        if (value) {
            variesByProductData[mda] = value;
        }
    });
    return variesByProductData;
};
const isNotEnsembleProduct = (itemEnsembles, definingAttributeValues) => {
    return itemEnsembles.every((itemAtt) => definingAttributeValues?.some((att) => itemAtt?.attribute?.name?.toLowerCase() === att?.attribute?.name?.toLowerCase() &&
        itemAtt?.value?.toLowerCase() === att?.value?.toLowerCase()));
};
function isValidProduct(inputObject) {
    return (typeof inputObject === 'object' &&
        inputObject !== null &&
        'product' in inputObject &&
        'inStock' in inputObject);
}
function isValidProductList(inputObject) {
    return (Array.isArray(inputObject) &&
        inputObject.length > 0 &&
        inputObject.some((item) => item && typeof item === 'object' && 'name' in item && 'href' in item));
}
function getImageFromProduct(product) {
    if (product.images && product.images.length > 0) {
        const images = [];
        if ('small' in product.images[0]) {
            images.push(product.images[0].small || '');
        }
        if ('medium' in product.images[0]) {
            images.push(product.images[0].medium || '');
        }
        if ('large' in product.images[0]) {
            images.push(product.images[0].large || '');
        }
        return images;
    }
    if ('image' in product && product.image) {
        return [
            (0, mosaic_1.mosaicUrl)(product.image, 100),
            (0, mosaic_1.mosaicUrl)(product.image, 232),
            (0, mosaic_1.mosaicUrl)(product.image, 348),
        ];
    }
    return [];
}
const getCategorySchema = (productDetails) => {
    const breadcrumbs = productDetails?.product?.breadcrumbs;
    return breadcrumbs ? breadcrumbs.map((breadcrumb) => breadcrumb?.name).join('>') : '';
};
//# sourceMappingURL=product-schema.js.map